import { useState, useCallback, useMemo } from 'react';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useLocation } from '@react-router';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { usePageDurationTimer } from '@shared/hooks/usePageDurationTimer';
import { isValid } from 'date-fns';
import { getLocalStorage } from '@shared/core';
import { REMINDER_MODAL_TRIGGER_DELAY_MS } from '@shared/components/ReminderModal/ReminderModal.constants';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventWithinReminderWindow } from '@shared/hooks/useEventWithinReminderWindow';
import { isInIframe } from '@shared/utils/isInIframe';

export const useReminderModalTrigger = (eventDate?: Date | null) => {
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [modalShownTimestamp, setModalShownTimestamp] = useState<string | null>(null);
  const { isAdmin } = useEventUserRole();
  const { eventHandle } = useEventInfo();
  const location = useLocation();
  const localStorage = getLocalStorage();
  const storageKey = `travel.guest.remindMeLaterShownDate.${eventHandle}`;

  const isEventWithinReminderWindow = useEventWithinReminderWindow(eventDate);

  const hasShownModalBefore = useMemo(() => {
    // Check the state first (for immediate updates)
    if (modalShownTimestamp) return true;

    // Use unique key for each event
    const storedData = localStorage.getItem(storageKey);
    if (!storedData) return false;

    try {
      const date = new Date(storedData);
      return isValid(date);
    } catch {
      // If parsing fails, return false
      return false;
    }
  }, [modalShownTimestamp, localStorage, storageKey]);

  const isOnAccommodationsPage = useMemo(() => {
    return location.pathname.includes('/accommodations');
  }, [location.pathname]);

  const [isFeatureFlagEnabled] = useIsFeatureEnabled('accommodationsRemindMeLaterExperiment', {
    skip: !isEventWithinReminderWindow || isAdmin || hasShownModalBefore || !isOnAccommodationsPage || isInIframe(),
  });

  const handleTimerFired = useCallback(() => {
    if (!eventHandle) return;

    setShowReminderModal(true);
    const timestamp = new Date().toISOString();

    // Use unique key for each event with simple date structure
    localStorage.setItem(storageKey, timestamp);
    setModalShownTimestamp(timestamp);
  }, [localStorage, eventHandle, storageKey]);

  // Use the common page duration timer hook
  usePageDurationTimer({
    durations: [
      {
        duration: REMINDER_MODAL_TRIGGER_DELAY_MS,
        callback: handleTimerFired,
        callbackName: 'reminderModalTrigger',
      },
    ],
    enabled: isFeatureFlagEnabled,
  });

  const handleReminderModalClose = useCallback(() => {
    setShowReminderModal(false);
  }, []);

  return {
    showReminderModal,
    handleReminderModalClose,
  };
};
